// The shop catalogue: static, versioned with the code (no server). Palettes are
// defined in src/lib/palette.ts; background themes live as CSS blocks in
// style.css (keyed by data-bg-theme). IDs are stable and stored in Firestore
// docs - never rename one. Prices from PLAN-shop-cosmetica.md (§5); first clear
// yields 15-25 coins, so a 150-300 item costs ~8-15 levels. That is deliberate.

import { PIPPIE_IDLE, PIPPIE_HAPPY } from "./pippie-mascot";

export type CosmeticType = "palette" | "background" | "mascot";

export interface ShopItem {
  id: string; // unique, stable, never renamed (lives in Firestore docs)
  type: CosmeticType;
  name: string; // Dutch display name
  price: number; // 0 = free (only for "default" items)
  description?: string;
  /** Mascots only: the emoji character shown on the home screen and win modal. */
  emoji?: string;
  /** Mascots only: inline-SVG mascot (idle state); takes precedence over emoji. */
  svg?: string;
  /** Mascots only: inline-SVG for the celebrating (win-modal) state. */
  svgWin?: string;
}

export const SHOP_ITEMS: ShopItem[] = [
  // --- Block color palettes ---
  { id: "default", type: "palette", name: "Candy", price: 0, description: "De vertrouwde standaardkleuren." },
  { id: "pastel", type: "palette", name: "Pastel", price: 150, description: "Zachte, lichte tinten." },
  { id: "neon", type: "palette", name: "Neon", price: 200, description: "Fel en verzadigd." },
  { id: "retro", type: "palette", name: "Retro", price: 200, description: "Aardse jaren-70 tinten." },
  { id: "oceaan", type: "palette", name: "Oceaan", price: 250, description: "Blauw, groen en zand." },
  { id: "contrast", type: "palette", name: "Hoog contrast", price: 100, description: "Extra goed onderscheidbaar." },

  // --- Background themes ---
  { id: "default", type: "background", name: "Warm bord", price: 0, description: "Het warme stippenpatroon." },
  { id: "nacht", type: "background", name: "Sterrennacht", price: 250, description: "Diep donkerblauw met sterren." },
  { id: "oceaan-bg", type: "background", name: "Oceaan", price: 250, description: "Blauwgroene diepte." },
  { id: "snoep", type: "background", name: "Snoeproze", price: 300, description: "Warm roze en perzik." },
  { id: "bos", type: "background", name: "Bos", price: 300, description: "Gedempt bladgroen." },
  { id: "ruimte", type: "background", name: "Ruimte", price: 400, description: "Bijna-zwart met felle glows." },

  // --- Mascots (cats!) - live on the home screen and celebrate your wins ---
  { id: "default", type: "mascot", name: "Geen mascotte", price: 0, description: "Lekker rustig." },
  { id: "kat", type: "mascot", name: "Kat", price: 100, emoji: "🐱", description: "Kijkt goedkeurend toe." },
  { id: "zwarte-kat", type: "mascot", name: "Zwarte kat", price: 150, emoji: "🐈‍⬛", description: "Brengt juist geluk." },
  { id: "blije-kat", type: "mascot", name: "Blije kat", price: 200, emoji: "😸", description: "Viert elk level mee." },
  { id: "verliefde-kat", type: "mascot", name: "Verliefde kat", price: 250, emoji: "😻", description: "Vindt jou de beste." },
  {
    id: "pippie",
    type: "mascot",
    name: "Pippie",
    price: 400,
    description: "De echte kat van het huis.",
    svg: PIPPIE_IDLE,
    svgWin: PIPPIE_HAPPY,
  },
];

/** The emoji of the equipped mascot, or null when none ("default") is equipped. */
export function getMascotEmoji(id: string): string | null {
  const item = SHOP_ITEMS.find((i) => i.type === "mascot" && i.id === id);
  return item?.emoji ?? null;
}

/**
 * Inline-SVG markup for a mascot, or null for emoji/"default" mascots (callers
 * then fall back to getMascotEmoji). `win` picks the celebrating state, falling
 * back to the idle SVG when a mascot has no dedicated win art.
 */
export function getMascotSvg(id: string, win = false): string | null {
  const item = SHOP_ITEMS.find((i) => i.type === "mascot" && i.id === id);
  if (!item?.svg) return null;
  return win ? item.svgWin ?? item.svg : item.svg;
}

/**
 * Look up a shop item. Note: the two "default" items share an id across the two
 * types; this returns the first match, which is fine because purchase logic
 * never routes through here (the shop passes the full item object).
 */
export function getItem(id: string): ShopItem | undefined {
  return SHOP_ITEMS.find((i) => i.id === id);
}
